import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader, ErrorState } from '@ui-kit';
import { reportDefinitionSchema } from '../schema/reportBuilder.schema';
import type { ReportDefinitionFormValues } from '../schema/reportBuilder.schema';
import { useReportBuilderDetail } from '../hooks/useReportBuilderDetail';
import { useCreateReportDef } from '../hooks/useCreateReportDef';
import { useUpdateReportDef } from '../hooks/useUpdateReportDef';
import { ReportPreviewDrawer } from '../components/ReportPreviewDrawer';
import { ColumnBuilderTable } from '../components/ColumnBuilderTable';
import { ReportCommonUsagePanel } from '../components/ReportCommonUsagePanel';
import type { ApiError, ReportColumnDto } from '../types/reportBuilder';

const DEFAULT_COLUMN: ReportColumnDto = {
  header: '',
  field: '',
  order: 0,
  widthRatio: 1.0,
  align: 'LEFT',
  barcodeMode: null,
  barcodeSourceField: null,
  barcodeEc: 'M',
};

/**
 * ReportEditorPage — 리포트 정의 생성/편집 페이지
 * /report-builder/new + /report-builder/{id}/edit
 * react-hook-form + zod resolver
 * 5상태: loading / error / empty(컬럼없음) / default / dialog(미리보기)
 */
export function ReportEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const reportId = id ? Number(id) : null;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<number | null>(reportId);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const detailQuery = useReportBuilderDetail(reportId);
  const createMutation = useCreateReportDef();
  const updateMutation = useUpdateReportDef();

  const form = useForm<ReportDefinitionFormValues>({
    resolver: zodResolver(reportDefinitionSchema),
    defaultValues: {
      name: '',
      description: '',
      dataSource: 'user',
      defaultFormat: 'EXCEL',
      columns: [],
      filters: [],
      shared: false,
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = form;

  const columns = watch('columns');

  // 편집 모드: 서버 데이터로 폼 초기화
  useEffect(() => {
    if (isEdit && detailQuery.data) {
      const d = detailQuery.data;
      reset({
        name: d.name,
        description: d.description ?? '',
        dataSource: d.dataSource as 'user' | 'audit-log' | 'static',
        defaultFormat: d.defaultFormat,
        columns: d.columns.map((c) => ({
          ...c,
          barcodeMode: c.barcodeMode ?? null,
          barcodeSourceField: c.barcodeSourceField ?? null,
          barcodeEc: c.barcodeEc ?? 'M',
        })),
        filters: d.filters ?? [],
        shared: d.shared,
      });
      setSavedId(d.id);
    }
  }, [isEdit, detailQuery.data, reset]);

  const handleAddColumn = useCallback(() => {
    const nextOrder = columns.length;
    setValue('columns', [...columns, { ...DEFAULT_COLUMN, order: nextOrder }], {
      shouldValidate: false,
    });
  }, [columns, setValue]);

  const onSubmit = useCallback(
    (values: ReportDefinitionFormValues) => {
      const body = {
        name: values.name,
        description: values.description || undefined,
        dataSource: values.dataSource,
        defaultFormat: values.defaultFormat,
        columns: values.columns.map((c) => ({
          ...c,
          barcodeMode: c.barcodeMode ?? null,
          barcodeSourceField: c.barcodeSourceField ?? null,
          barcodeEc: c.barcodeEc ?? 'M',
        })),
        filters: values.filters ?? [],
        shared: values.shared,
      };

      if (isEdit && reportId) {
        return updateMutation.mutateAsync({ id: reportId, body }).then((res) => {
          setSavedId(res.id);
          showToast(t('reportBuilder.toast.saved'));
          setTimeout(() => navigate('/report-builder'), 1200);
        }).catch((err: ApiError) => {
          showToast(
            t(`reportBuilder.error.${err.code}`, {
              defaultValue: t('reportBuilder.editor.error.saveFailed'),
            })
          );
        });
      } else {
        return createMutation.mutateAsync(body).then((res) => {
          setSavedId(res.id);
          showToast(t('reportBuilder.toast.saved'));
          setTimeout(() => navigate('/report-builder'), 1200);
        }).catch((err: ApiError) => {
          showToast(
            t(`reportBuilder.error.${err.code}`, {
              defaultValue: t('reportBuilder.editor.error.saveFailed'),
            })
          );
        });
      }
    },
    [isEdit, reportId, createMutation, updateMutation, navigate, showToast, t]
  );

  const isBusy = isSubmitting || createMutation.isPending || updateMutation.isPending;

  // loading skeleton (편집 모드)
  if (isEdit && detailQuery.isLoading) {
    return (
      <div
        aria-label={t('reportBuilder.editor.loadingAriaLabel')}
        aria-busy="true"
        style={{ padding: 'var(--spacing-6)' }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            style={{
              height: 40,
              background: 'var(--color-surface-elevated)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--spacing-3)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        ))}
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
      </div>
    );
  }

  // error (편집 모드)
  if (isEdit && detailQuery.isError) {
    return (
      <div style={{ padding: 'var(--spacing-6)' }}>
        <ErrorState
          title={t('reportBuilder.editor.error.loadFailed')}
          message={(detailQuery.error as unknown as ApiError)?.message ?? t('reportBuilder.error.network')}
          onRetry={() => void detailQuery.refetch()}
        />
      </div>
    );
  }

  const titleKey = isEdit ? 'reportBuilder.editor.title.edit' : 'reportBuilder.editor.title.new';

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t(titleKey)}
        breadcrumb={
          <span>
            <button
              type="button"
              onClick={() => navigate('/report-builder')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-brand-primary)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                padding: 0,
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('reportBuilder.list.title')}
            </button>
            {' > '}
            {isEdit ? t('reportBuilder.editor.breadcrumb.edit') : t('reportBuilder.editor.breadcrumb.new')}
          </span>
        }
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
            {/* 미리보기 */}
            <button
              type="button"
              disabled={savedId === null}
              title={savedId === null ? '저장 후 미리보기 가능합니다' : undefined}
              onClick={() => setPreviewOpen(true)}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-surface-elevated)',
                color: savedId === null ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: savedId === null ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                opacity: savedId === null ? 0.5 : 1,
              }}
            >
              {t('reportBuilder.editor.button.preview')}
            </button>

            {/* 저장 */}
            <button
              type="submit"
              form="report-editor-form"
              disabled={isBusy}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: isBusy ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                color: isBusy ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: isBusy ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {isBusy ? '...' : t('reportBuilder.editor.button.save')}
            </button>

            {/* 취소 */}
            <button
              type="button"
              onClick={() => navigate('/report-builder')}
              disabled={isBusy}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'transparent',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('reportBuilder.editor.button.cancel')}
            </button>
          </div>
        }
      />

      {/* 폼 */}
      <form
        id="report-editor-form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        aria-label={t(titleKey)}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-5)' }}
      >
        {/* 기본 정보 섹션 */}
        <div
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-5)',
            background: 'var(--color-surface-background)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 'var(--spacing-4)',
            }}
          >
            {/* 이름 */}
            <div>
              <label
                htmlFor="field-name"
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  marginBottom: 'var(--spacing-1)',
                  fontWeight: 600,
                }}
              >
                {t('reportBuilder.editor.field.name')} *
              </label>
              <input
                id="field-name"
                type="text"
                {...register('name')}
                placeholder={t('reportBuilder.editor.field.namePlaceholder')}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'field-name-err' : undefined}
                disabled={isBusy}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: `1px solid ${errors.name ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-sans)',
                }}
              />
              {errors.name && (
                <p
                  id="field-name-err"
                  role="alert"
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-danger)',
                    marginTop: 'var(--spacing-1)',
                  }}
                >
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* 설명 */}
            <div>
              <label
                htmlFor="field-desc"
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  marginBottom: 'var(--spacing-1)',
                  fontWeight: 600,
                }}
              >
                {t('reportBuilder.editor.field.description')}
              </label>
              <input
                id="field-desc"
                type="text"
                {...register('description')}
                placeholder={t('reportBuilder.editor.field.descriptionPlaceholder')}
                disabled={isBusy}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-sans)',
                }}
              />
            </div>

            {/* 데이터 소스 */}
            <div>
              <label
                htmlFor="field-dataSource"
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  marginBottom: 'var(--spacing-1)',
                  fontWeight: 600,
                }}
              >
                {t('reportBuilder.editor.field.dataSource')} *
              </label>
              <select
                id="field-dataSource"
                {...register('dataSource')}
                aria-invalid={!!errors.dataSource}
                disabled={isBusy}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: `1px solid ${errors.dataSource ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                  cursor: 'pointer',
                }}
              >
                <option value="user">{t('reportBuilder.dataSource.user')}</option>
                <option value="audit-log">{t('reportBuilder.dataSource.audit-log')}</option>
                <option value="static">{t('reportBuilder.dataSource.static')}</option>
              </select>
              {errors.dataSource && (
                <p role="alert" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: 'var(--spacing-1)' }}>
                  {errors.dataSource.message}
                </p>
              )}
            </div>

            {/* 기본 포맷 */}
            <fieldset
              style={{ border: 'none', padding: 0, margin: 0 }}
            >
              <legend
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  marginBottom: 'var(--spacing-2)',
                  fontWeight: 600,
                }}
              >
                {t('reportBuilder.editor.field.defaultFormat')} *
              </legend>
              <div style={{ display: 'flex', gap: 'var(--spacing-4)', alignItems: 'center' }}>
                {(['PDF', 'EXCEL'] as const).map((fmt) => (
                  <label
                    key={fmt}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-1)',
                      fontSize: 'var(--font-size-sm)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      value={fmt}
                      {...register('defaultFormat')}
                      disabled={isBusy}
                    />
                    {t(`reportBuilder.format.${fmt}`)}
                  </label>
                ))}
              </div>
            </fieldset>

            {/* 공유 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
              <input
                id="field-shared"
                type="checkbox"
                {...register('shared')}
                disabled={isBusy}
                style={{ cursor: 'pointer', width: 16, height: 16 }}
              />
              <label
                htmlFor="field-shared"
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-primary)',
                  cursor: 'pointer',
                }}
              >
                {t('reportBuilder.editor.field.shared')}
              </label>
            </div>
          </div>
        </div>

        {/* 컬럼 구성 섹션 */}
        <div
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            background: 'var(--color-surface-background)',
          }}
        >
          {/* 헤더 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'var(--color-surface-muted)',
              borderBottom: '1px solid var(--color-surface-border)',
            }}
          >
            <h2
              style={{
                fontSize: 'var(--font-size-md)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              {t('reportBuilder.editor.columns.title')}
            </h2>
            <button
              type="button"
              onClick={handleAddColumn}
              disabled={isBusy}
              style={{
                padding: 'var(--spacing-1) var(--spacing-3)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              + {t('reportBuilder.editor.columns.add')}
            </button>
          </div>

          {/* 컬럼 표 */}
          <div style={{ padding: 'var(--spacing-3)' }}>
            <ColumnBuilderTable form={form} onAdd={handleAddColumn} />
          </div>

          {/* 컬럼 검증 에러 */}
          {errors.columns && !Array.isArray(errors.columns) && (
            <p
              role="alert"
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-danger)',
                padding: 'var(--spacing-3) var(--spacing-4)',
                margin: 0,
              }}
            >
              {errors.columns.message}
            </p>
          )}
        </div>
      </form>

      {/* 공통 모듈 패널 */}
      <ReportCommonUsagePanel />

      {/* 미리보기 Drawer (dialog 상태) */}
      {previewOpen && savedId !== null && (
        <ReportPreviewDrawer
          reportId={savedId}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          form { gap: var(--spacing-3); }
        }
      `}</style>
    </div>
  );
}
